import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { WAHAWebhookEvent, WAHAEventType } from '@/services/interface';
import { getEnv } from '@/config/envs';

type EventHandler = (event: WAHAWebhookEvent) => void;

interface EventSubscription {
  eventType: WAHAEventType | 'all';
  handler: EventHandler;
  id: string;
}

interface ReconnectConfig {
  maxAttempts: number;
  initialDelay: number;
  maxDelay: number;
}

interface UseWhatsappEventsOptions {
  wsUrl?: string;
  reconnectConfig?: ReconnectConfig;
}

/**
 * Hook for managing WebSocket connections and webhook events from WAHA
 */
export function useWhatsappEvents(
  sessionName: string = 'default',
  options: UseWhatsappEventsOptions = {},
) {
  // Stabilize configuration options
  const wsUrl = useMemo(
    () => options.wsUrl || getEnv().VITE_WHATSAPP_SERVER_WEBSOCKET_URL,
    [options.wsUrl],
  );

  const reconnectConfig = useMemo(
    () =>
      options.reconnectConfig || {
        maxAttempts: 10,
        initialDelay: 1000, // 1 second
        maxDelay: 30000, // 30 seconds
      },
    [options.reconnectConfig],
  );

  // State and refs
  const [isConnected, setIsConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState<WAHAWebhookEvent | null>(null);
  const [messages, setMessages] = useState<WAHAWebhookEvent[]>([]);
  const [statusEvents, setStatusEvents] = useState<WAHAWebhookEvent[]>([]);

  const wsRef = useRef<WebSocket | null>(null);
  const eventHandlersRef = useRef<EventSubscription[]>([]);
  const reconnectTimerRef = useRef<number | null>(null);
  const reconnectAttemptsRef = useRef<number>(0);
  const queryClient = useQueryClient();
  const connectionAttemptInProgressRef = useRef(false);

  // Calculate exponential backoff delay
  const getReconnectDelay = useCallback(() => {
    const { initialDelay, maxDelay } = reconnectConfig;
    const attempts = reconnectAttemptsRef.current;

    // Calculate exponential backoff with jitter
    const exponentialDelay = Math.min(
      maxDelay,
      initialDelay * Math.pow(2, attempts),
    );

    // Add some randomness to prevent all clients reconnecting simultaneously
    const jitter = 0.2; // 20% jitter
    const randomizedDelay =
      exponentialDelay * (1 + jitter * (Math.random() - 0.5));

    return Math.floor(randomizedDelay);
  }, [reconnectConfig]);

  // Process incoming events
  const processEvent = useCallback(
    (event: WAHAWebhookEvent) => {
      // Filter out message.any events - we only want to process 'message' events
      if (event.event === 'message.any') {
        console.log(`[processEvent] Ignoring message.any event: ${event.id}`);
        return;
      }

      setLastEvent(event);

      // Store specific event types
      if (event.event === 'message') {
        setMessages((prev) => [event, ...prev.slice(0, 49)]); // Keep last 50 messages
      } else if (event.event === 'session.status') {
        setStatusEvents((prev) => [event, ...prev.slice(0, 9)]); // Keep last 10 status events
      }

      // Update React Query cache based on event type
      switch (event.event) {
        case 'message':
          // TODO: CHECK this have a delay if message come
          queryClient.invalidateQueries({
            queryKey: ['messages', event.session],
          });

          console.log('event - message', event);

          break;
        case 'session.status':
          queryClient.invalidateQueries({
            queryKey: ['whatsappSessionStatus', event.session],
          });
          queryClient.invalidateQueries({ queryKey: ['channels'] });
          break;
        case 'message.ack':
          queryClient.invalidateQueries({
            queryKey: ['messages', event.session, event.payload?.to],
          });
          break;
        case 'presence.update':
          queryClient.invalidateQueries({
            queryKey: ['contact', event.session, event.payload?.id],
          });
          break;
      }

      // Call all registered handlers for this event type
      eventHandlersRef.current.forEach((subscription) => {
        if (
          subscription.eventType === 'all' ||
          subscription.eventType === event.event
        ) {
          subscription.handler(event);
        }
      });
    },
    [queryClient],
  );

  // Clean up WebSocket and timers
  const cleanupWebSocket = useCallback(() => {
    console.log('[CLEANUP] Starting WebSocket cleanup');

    // Clear any pending reconnect timers
    if (reconnectTimerRef.current) {
      console.log('[CLEANUP] Clearing reconnect timer');
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }

    // Close and clean up the WebSocket
    if (wsRef.current) {
      console.log(
        `[CLEANUP] Closing WebSocket (readyState: ${wsRef.current.readyState})`,
      );

      // Remove all event handlers to prevent them from firing during close
      wsRef.current.onopen = null;
      wsRef.current.onmessage = null;
      wsRef.current.onerror = null;
      wsRef.current.onclose = null;

      // Close the connection
      wsRef.current.close();
      wsRef.current = null;
    }

    console.log('[CLEANUP] WebSocket cleanup complete');
  }, []);

  // Connect to WebSocket with proper error handling
  const connectWebSocket = useCallback(() => {
    // Only clean up if we have a completed connection
    // Don't clean up connections in CONNECTING (0) state
    if (wsRef.current && wsRef.current.readyState !== 0) {
      cleanupWebSocket();
    } else if (wsRef.current) {
      console.log('[CONNECT] WebSocket already connecting, not interrupting');
      return; // Don't interrupt an in-progress connection
    }

    try {
      // Get API key from environment - check widget context first
      const apiKey = getEnv().VITE_WHATSAPP_SERVER_API_KEY;

      // Properly format WebSocket URL with needed parameters
      const queryParams = new URLSearchParams({
        'x-api-key': apiKey,
        session: sessionName,
      });

      // Add events parameters - explicitly only subscribe to 'message' not 'message.any'
      const events = ['message', 'session.status'];
      events.forEach((event) => {
        queryParams.append('events', event);
      });

      const fullWsUrl = `${wsUrl}?${queryParams.toString()}`;

      console.log(
        `[CONNECT] Attempting WebSocket connection to ${fullWsUrl} (attempt ${reconnectAttemptsRef.current + 1})`,
      );

      const ws = new WebSocket(fullWsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[OPEN] WebSocket connection established');
        setIsConnected(true);
        reconnectAttemptsRef.current = 0;
        connectionAttemptInProgressRef.current = false;
      };

      ws.onmessage = (messageEvent) => {
        try {
          const event = JSON.parse(messageEvent.data) as WAHAWebhookEvent;
          console.log(
            `[WebSocket] Event received for session '${event.session}':`,
            {
              type: event.event,
              payload: event.payload,
              matchesSession: event.session === sessionName,
            },
          );

          if (event.session === sessionName || !event.session) {
            processEvent(event);
          }
        } catch (error) {
          console.error('[MESSAGE] Error parsing WebSocket message:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('[ERROR] WebSocket error:', error);
        setIsConnected(false);
      };

      ws.onclose = (closeEvent) => {
        console.log(
          `[CLOSE] WebSocket closed with code ${closeEvent.code} and reason: ${closeEvent.reason || 'No reason provided'}`,
        );
        setIsConnected(false);

        // Only attempt to reconnect if this is still the current WebSocket
        // and there's no reconnect timer already running
        if (wsRef.current === ws && !reconnectTimerRef.current) {
          const { maxAttempts } = reconnectConfig;

          if (reconnectAttemptsRef.current < maxAttempts) {
            const delay = getReconnectDelay();
            reconnectAttemptsRef.current += 1;

            console.log(
              `[RECONNECT] Scheduling reconnect in ${delay}ms (attempt ${reconnectAttemptsRef.current} of ${maxAttempts})`,
            );

            reconnectTimerRef.current = window.setTimeout(() => {
              reconnectTimerRef.current = null;
              connectWebSocket();
            }, delay);
          } else {
            console.error(
              `[MAX_ATTEMPTS] Maximum reconnection attempts (${maxAttempts}) reached.`,
            );
          }
        }
      };
    } catch (error) {
      console.error('[ERROR] Error setting up WebSocket connection:', error);

      // Schedule reconnect for critical initialization errors
      if (
        !reconnectTimerRef.current &&
        reconnectAttemptsRef.current < reconnectConfig.maxAttempts
      ) {
        const delay = getReconnectDelay();
        reconnectAttemptsRef.current += 1;

        console.log(
          `[CRITICAL] Scheduling reconnect after critical error in ${delay}ms`,
        );

        reconnectTimerRef.current = window.setTimeout(() => {
          reconnectTimerRef.current = null;
          connectWebSocket();
        }, delay);
      }
    }
  }, [
    sessionName,
    wsUrl,
    processEvent,
    reconnectConfig,
    getReconnectDelay,
    cleanupWebSocket,
  ]);

  // Manual reconnect function that can be exposed to users
  const reconnect = useCallback(() => {
    console.log('[RECONNECT] Manual reconnection requested');
    cleanupWebSocket();
    reconnectAttemptsRef.current = 0;
    connectWebSocket();
  }, [cleanupWebSocket, connectWebSocket]);

  // Subscribe to events
  const subscribe = useCallback(
    (eventType: WAHAEventType | 'all', handler: EventHandler) => {
      const id = Math.random().toString(36).substring(2, 9);
      const subscription: EventSubscription = { eventType, handler, id };

      eventHandlersRef.current.push(subscription);
      console.log(
        `[SUBSCRIBE] Added handler for ${eventType} events (id: ${id})`,
      );
      return id;
    },
    [],
  );

  // Unsubscribe from events
  const unsubscribe = useCallback((id: string) => {
    const beforeLength = eventHandlersRef.current.length;
    eventHandlersRef.current = eventHandlersRef.current.filter(
      (subscription) => subscription.id !== id,
    );

    const removed = beforeLength > eventHandlersRef.current.length;
    if (removed) {
      console.log(`[UNSUBSCRIBE] Removed handler with id: ${id}`);
    } else {
      console.warn(`[UNSUBSCRIBE] No handler found with id: ${id}`);
    }
  }, []);

  // Process webhook event (for server-side webhook handling)
  const processWebhookEvent = useCallback(
    (webhookData: WAHAWebhookEvent) => {
      console.log('[WEBHOOK] Processing webhook event', webhookData.event);
      processEvent(webhookData);
    },
    [processEvent],
  );

  // Set up WebSocket connection when the hook initializes
  useEffect(() => {
    console.log('[EFFECT] Setting up WhatsApp events connection');

    // Only connect if we're not already attempting to connect
    if (!connectionAttemptInProgressRef.current) {
      connectionAttemptInProgressRef.current = true;
      connectWebSocket();
    }

    // Cleanup function for when the component unmounts or the effect re-runs
    return () => {
      console.log('[EFFECT] Cleaning up WhatsApp events connection');
      connectionAttemptInProgressRef.current = false;
      cleanupWebSocket();
      reconnectAttemptsRef.current = 0;
    };
  }, [connectWebSocket, cleanupWebSocket]);

  return {
    isWebSocketOpen: wsRef?.current?.readyState === 1,
    isConnected,
    lastEvent,
    messages,
    statusEvents,
    subscribe,
    unsubscribe,
    processWebhookEvent,
    reconnect,
    wsRef,
  };
}

// Expose reconnect as a static method
useWhatsappEvents.reconnect = null;
